import { useUser } from "@/composables/user/user";
import { createCollectorLink, createError404Link } from "@/router/linkFactories";
import { RouteName } from "@/router/routeNameEnum";
import { checkUserOnInitialPageLoad } from "@/services/user/auth/login/helpers";
import { fetchUserProfile } from "@/services/user/profile/profileApi";
import { UserNotFoundException } from "@/services/user/userExceptions";
import { type NavigationGuardNext, type RouteLocationNormalized } from "vue-router";

/**
 * `/me` redirects the logged-in user to their own public profile.
 * Must be registered after handleLogin so logged-out visitors go through the login first.
 */
export const handleMeRedirect = async (toRoute: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
  if (toRoute.name !== RouteName.Me) {
    return next();
  }

  await checkUserOnInitialPageLoad();
  const { userId } = useUser();
  if (userId.value === undefined) {
    return next(); // Logged out - handleLogin already redirected to auth.
  }

  try {
    const profile = await fetchUserProfile({ userId: userId.value });
    return next({ ...(createCollectorLink(profile.nickname) as object), replace: true });
  } catch (e: unknown) {
    if (e instanceof UserNotFoundException) {
      return next(createError404Link());
    }
    throw e;
  }
};
